import { useEffect, useState } from "react";
import { put } from "../../../common/api/apiShared";
import { Result } from "../../../common/api/result";
import { useAllSelectableUsersQuery, useSelectableDocumentTypesQueryByUsageType } from "../../../common/hooks/useSelectableItemQueries";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useValidator } from "../../../common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { LegalEntityDocumentUpdateModel } from "../LegalEntityDocumentTab.types";
import { useDocumentHook } from "../../../documents/Documents.hook";
import { IEditLegalEntityDocumentDialogProps } from "./EditLegalEntityDocumentDialog";
import { LegalEntityDocumentViewModel } from "./EditLegalEntityDocumentDialog.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useEditLegalEntityDocumentHook(props: IEditLegalEntityDocumentDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { gdpo } = useUserContext();

    const [document, setDocument] = useState<LegalEntityDocumentUpdateModel>();
    const [documentsUploading, setDocumentsUploading] = useState(false);

    const { data, isLoading } = useAllSelectableUsersQuery(props.isExtended);
    const url = `/legalEntity/${props.legalEntityId}/document/${props.documentId}`;
    const legalEntityDocumentQuery = useDotLegalQuery<LegalEntityDocumentViewModel>(url);

    var legalEntityDocumentData = legalEntityDocumentQuery.data;
    const { data: documentTypes, isLoading: documentTypesIsLoading } = useSelectableDocumentTypesQueryByUsageType(props.documentTypeUsageType);

    useEffect(() => {
        if (legalEntityDocumentData) {
            setDocument({
                documentId: legalEntityDocumentData!.documentId,
                name: legalEntityDocumentData!.name,
                ownerId: legalEntityDocumentData!.owner,
                documentTypeId: legalEntityDocumentData!.documentTypeId,
                addDocumentToSharings: [],
                fileName: "",
                isLink: false,
                link: "",
                status: legalEntityDocumentData.status,
                groupEntityAccessIds: legalEntityDocumentData.groupEntityAccessIds,
                allGroupEntities: legalEntityDocumentData.allGroupEntities,
            });
        }
    }, [legalEntityDocumentData]);

    const validation = useValidator<LegalEntityDocumentUpdateModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            groupEntityAccessIds: validators.validateNotEmpty(
                (item) => item.groupEntityAccessIds,
                "",
                (item) => item.allGroupEntities,
                translateString("oneGroupCompanyIsRequired")
            ),
        }),
        []
    );

    let documentStatus = useDocumentHook().selecableDocumentStatus;

    const editMutation = useDotLegalMutation(getEditMutation);

    function getEditMutation(doc: LegalEntityDocumentUpdateModel) {
        return put<string>(`/LegalEntity/${doc.documentId}/Documents`, doc);
    }

    const editDocument = () => {
        editMutation.mutateAsync(document!, {
            onSuccess: (response: Result<string>) => {
                snackbar.show(translateString("documentWasUpdated"));

                if (props.onSave) {
                    props.onSave();
                }

                props.onCloseDialog();
            },
        });
    };

    function onGroupEntityAccessIdsChange(groupEntityIds: Array<string>) {
        let tempModel = { ...document! };
        tempModel.groupEntityAccessIds = groupEntityIds;

        if (groupEntityIds.length > 0) {
            tempModel.allGroupEntities = false;
        }

        setDocument(tempModel);
    }

    function onAllGroupEntitiesChange(allGroupEntities: boolean) {
        let tempModel = { ...document! };
        tempModel.allGroupEntities = allGroupEntities;

        if (allGroupEntities) {
            tempModel.groupEntityAccessIds = [];
        }

        setDocument(tempModel);
    }

    return {
        document,
        setDocument,
        documentTypes,
        inProgress: editMutation.isLoading,
        validation,
        validateDocumentForm,
        userOptions: data,
        userOptionsIsLoading: isLoading,
        isLoading: legalEntityDocumentQuery.isLoading,
        documentsUploading,
        setDocumentsUploading,
        documentStatus,
        onGroupEntityAccessIdsChange,
        onAllGroupEntitiesChange,
        gdpo,
        documentTypesIsLoading,
    };

    function validateDocumentForm() {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }
        editDocument();
    }
}
